import React from "react";
import Barcode from "react-barcode";

export const AdmitCardTemplate = React.forwardRef(({ data, schoolInfo }, ref) => {
    if (!data) return null;

    console.log("dta", data)

    const { student, class: studentClass, rollNumber, examType, examRoutine, academicSession, serialNumber } = data;

    // Generate QR code data
    const qrData = `${rollNumber}-${examType?.name}-${academicSession?.label}`;

    return (
        <div
            ref={ref}
            className="w-[210mm] min-h-[297mm] bg-white p-6 mx-auto font-sans text-gray-900 border-2 border-gray-800 print:border-2"
        >

            {/* --- TOP SECTION: Barcode, Logo, Photo --- */}
            <div className="relative flex justify-center items-start mb-3 min-h-[120px]">
                {/* Left: Serial Number & Barcode */}
                <div className="absolute left-0 top-0 text-left">
                    <div className="text-[10px] font-bold mb-1">Serial No: {serialNumber || rollNumber}</div>
                    <Barcode
                        value={serialNumber || rollNumber || "000000"}
                        width={1.2}
                        height={35}
                        fontSize={8}
                        displayValue={false}
                    />
                </div>

                {/* Center: School Logo & Name */}
                <div className="text-center px-4 max-w-[60%] z-10">
                    {schoolInfo?.logo && (
                        <img
                            src={schoolInfo.logo}
                            alt="School Logo"
                            className="h-16 w-auto mx-auto mb-1 object-contain"
                        />
                    )}
                    <h1 className="text-xl font-bold text-blue-900 uppercase leading-tight">
                        {schoolInfo?.name || "School Name"}
                    </h1>
                    {schoolInfo?.tagLine && (
                        <p className="text-xs italic text-gray-600 mt-0.5">{schoolInfo.tagLine}</p>
                    )}
                </div>

                {/* Right: Student Photo */}
                <div className="absolute right-0 top-0 flex-shrink-0 w-24 h-28 border-2 border-gray-800 overflow-hidden bg-gray-100">
                    {student?.profilePicture ? (
                        <img
                            src={student.profilePicture}
                            className="w-full h-full object-cover"
                            alt="Student"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-500 font-bold text-center">
                            AFFIX<br />PASSPORT<br />PHOTO
                        </div>
                    )}
                </div>
            </div>

            {/* --- EXAM TITLE --- */}
            <div className="text-center mb-2">
                <h2 className="text-sm font-bold">
                    {examType?.name || "Examination"}, {academicSession?.label || new Date().getFullYear()}
                </h2>
            </div>

            {/* --- ADMIT CARD HEADING --- */}
            <div className="text-center mb-3">
                <h3 className="text-base font-bold underline">Admit Card</h3>
            </div>

            {/* --- QR CODE --- */}
            <div className="flex justify-center mb-3">
                <div className="border-2 border-gray-800 p-1.5">
                    <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(qrData)}`}
                        alt="QR Code"
                        className="w-24 h-24"
                    />
                </div>
            </div>

            {/* --- STUDENT INFORMATION --- */}
            <div className="mb-3">
                <h4 className="text-xs font-bold mb-1.5 text-center">Student Information</h4>
                <table className="w-full border-collapse border border-gray-800 text-[10px]">
                    <tbody>
                        <tr className="border-b border-t border-gray-800">
                            <td className="font-bold w-1/3 border-r border-b border-gray-800 bg-gray-50">Name</td>
                            <td className="p-1 font-bold uppercase">{student?.firstName} {student?.lastName}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                            <td className="p-1 font-bold border-r border-b border-gray-800 bg-gray-50">Enrollment Number</td>
                            <td className="p-1 font-bold">{rollNumber}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                            <td className="p-1 font-bold border-r border-b border-gray-800 bg-gray-50">Course</td>
                            <td className="p-1">{studentClass?.name || "N/A"}</td>
                        </tr>
                        <tr className="border-b border-gray-800">
                            <td className="p-1 font-bold border-r border-b border-gray-800 bg-gray-50">Session</td>
                            <td className="p-1">{academicSession?.label || "N/A"}</td>
                        </tr>
                        <tr>
                            <td className="p-1 font-bold border-r border-b border-gray-800 bg-gray-50">Appearing for</td>
                            <td className="p-1">{examType?.name || "N/A"}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* --- ELIGIBLE COURSES / EXAM SCHEDULE --- */}
            <div className="mb-3">
                <h4 className="text-xs font-bold mb-1.5 text-center">Eligible Courses</h4>
                <table className="w-full border-collapse border border-gray-800 text-[9px]">
                    <tbody>
                        {examRoutine && examRoutine.length > 0 ? (
                            examRoutine.map((exam, idx) => (
                                <tr key={idx} className="border-b border-gray-800">
                                    <td className="p-1">
                                        {exam.subjectName} {exam.subjectCode ? `(${exam.subjectCode})` : ""}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className="p-2 text-center text-gray-500">No courses found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- INSTRUCTIONS --- */}
            <div className="mb-4 border border-gray-800 p-2">
                <h4 className="text-[10px] font-bold mb-1">Instructions to Students:</h4>
                <ul className="list-disc list-inside text-[8px] space-y-0.5 leading-tight">
                    <li>Students must bring their Admit Cards to enter the examination hall for all days of Examination. Admission to examination hall may ordinarily be refused to a candidate who is more than half an hour late.</li>
                    <li>No candidate shall be permitted to leave the Examination Hall until an hour after the commencement of the examination.</li>
                    <li>Students may keep necessary amenities like pens, pencil, rubber, mathematical boxes, etc. with them. However they will be allowed to use battery operated simple pocket calculator as per the nature of question paper only.</li>
                    <li>Candidates should not bring any articles (other than those specified above) such as books, notes, loose sheets, mobile phones, papers, digital diaries, etc. to the Examination Hall.</li>
                    <li>Candidates found using any kind of unfair means during the examination shall be expelled by the competent authority.</li>
                    <li>Students are not allowed to write anything on the admit card except putting signature on the admit card. If any difficulty found during the examination, student should contact directly to the invigilator only.</li>
                </ul>
            </div>

            {/* --- FOOTER --- */}
            <div className="flex justify-between items-end text-[10px] mt-4">
                <div>
                    <p className="font-bold">Issued Date: {new Date().toLocaleDateString('en-GB')}</p>
                </div>
                <div className="text-right">
                    {schoolInfo?.signatureUrl && (
                        <img
                            src={schoolInfo.signatureUrl}
                            alt="Signature"
                            className="h-8 w-auto ml-auto mb-1"
                        />
                    )}
                    <p className="font-bold text-[9px]">Sd/-</p>
                    <p className="font-bold text-[9px]">Controller of Examination</p>
                </div>
            </div>

            {/* --- COMPUTER GENERATED MESSAGE --- */}
            <div className="mt-3 pt-2 border-t-2 border-gray-800">
                <p className="text-[10px] text-gray-900 font-bold text-center">
                    *** This is a computer generated admit card. No need for manual verification. ***
                </p>
            </div>

            {/* --- DISCLAIMER FOOTER --- */}
            <div className="mt-1">
                <p className="text-[8px] text-gray-700 italic text-center">
                    Copyright @{schoolInfo?.name}. Any duplication of this Admit Card will be considered as a serious issue. Student may be debarred from the examination if found so.
                </p>
            </div>

        </div>
    );
});

export default AdmitCardTemplate;
